import clsx from 'clsx'
import { Trash2 } from 'lucide-react'
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState
} from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import Avatar from '@/components-latest/primitives/Avatar'
import Button from '@/components-latest/primitives/Button'
import ContentBlock from '@/components-latest/primitives/ContentBlock'
import Checkbox from '@/components-latest/primitives/Controls/Checkbox'
import RadioGroup from '@/components-latest/primitives/Controls/RadioGroup'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components-latest/primitives/Controls/Select'
import { Plus } from '@/components-latest/primitives/Icons'
import Label from '@/components-latest/primitives/Label'
import ScrollArea from '@/components-latest/primitives/Scrollable'
import Separator from '@/components-latest/primitives/Separator'
import Text from '@/components-latest/primitives/Text'
import { useLazyFetchMenuListQuery } from '@/ducks/menu/api/menu.api'
import {
  selectCurrencyCode,
  selectStoreId
} from '@/ducks/store/selectors/store.selectors'
import { useServerTranslation } from '@/hooks/useServerTranslation/useServerTranslation'
import { useToggle } from '@/hooks/useToggle/useToggle'

import SelectMenuItemsModal from '../OfferDetailsForm/SelectMenuItemsModal'
import Styles from './FormDetails.module.scss'
type DefaultValues = {
  selectedMenuItem?: string[]
  selectedMenuItemY?: string[]
  xItemQuantity?: string
  yItemQuantity?: string
  discount: number
  expirationValue?: string
  isCumulative?: boolean
  selectedItemName?: string
  selectedItemYName?: string
}
type Props = {
  className?: string
  onFormChange?: () => void // Callback to notify parent of form changes
  defaultValues?: DefaultValues
}

export type FormDetailsRef = {
  isFormValid: () => boolean
  selectedMenuItem: string[]
  selectedMenuItemY: string[]
  xItemQuantity: string
  yItemQuantity: string
  discount: string
  expirationValue: string
  isCumulative: boolean
  selectedItemName: string // Add selectedItemName
  selectedItemYName: string // Add selectedItemYName
}

const FormDetails = forwardRef<FormDetailsRef, Props>(
  ({ className, onFormChange, defaultValues }, ref) => {
    FormDetails.displayName = 'FormDetails'
    const { t } = useTranslation()
    const dummyQuantity = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10]
    const DISCOUNT_VALUES = [50, 75, 100]
    const expirationValues = useMemo(
      () => ['3h', '12h', '24h', '3d', '7d', '15d', '30d'],
      []
    )
    const [requestMenu, { data }] = useLazyFetchMenuListQuery()
    const storeId = useSelector(selectStoreId)
    const [xItemQuantity, setXItemQuantity] = useState(
      defaultValues?.xItemQuantity || '1'
    )
    const [yItemQuantity, setYItemQuantity] = useState(
      defaultValues?.yItemQuantity || '1'
    )
    const [discount, setDiscount] = useState<string>(
      String(defaultValues?.discount || 50)
    )

    const [expirationValue, setExpirationValue] = useState(
      defaultValues?.expirationValue || '3h'
    )
    const [isCumulative, setIsCumulative] = useState(
      defaultValues?.isCumulative || false
    )
    const [isSelectItemsModalOpen, toggleSelectItemsModalOpen] =
      useToggle(false)
    const [isSelectItemsYModalOpen, toggleSelectItemsYModalOpen] =
      useToggle(false)
    const [selectedMenuItem, setSelectedMenuItem] = useState<string[]>(
      defaultValues?.selectedMenuItem || []
    )
    const currency = useSelector(selectCurrencyCode)
    const [selectedMenuItemY, setSelectedMenuItemY] = useState<string[]>(
      defaultValues?.selectedMenuItemY || []
    )

    useEffect(() => {
      if (storeId) {
        requestMenu({ store_id: storeId })
      }
    }, [requestMenu, storeId])
    const findMenuItemById = (id: string) =>
      data?.menu?.mainCategories
        ?.flatMap(category =>
          category.items.concat(
            category.subCategories.flatMap(sub => sub.items)
          )
        )
        .find(item => item._id === id)

    const selectedItem = findMenuItemById(selectedMenuItem[0])
    const selectedItemY = findMenuItemById(selectedMenuItemY[0])
    const { getTranslation } = useServerTranslation()

    const isFormValid = () => {
      return !!(
        xItemQuantity &&
        yItemQuantity &&
        discount &&
        expirationValue &&
        selectedMenuItem.length > 0 &&
        selectedMenuItemY.length > 0 &&
        parseInt(xItemQuantity) > 0 &&
        parseInt(yItemQuantity) > 0
      )
    }

    useImperativeHandle(ref, () => ({
      isFormValid,
      selectedMenuItem,
      selectedMenuItemY,
      xItemQuantity,
      yItemQuantity,
      discount,
      expirationValue,
      isCumulative,
      selectedItemName: selectedItem?.name?.en || '',
      selectedItemYName: selectedItemY?.name?.en || ''
    }))

    // Notify parent of form changes
    useEffect(() => {
      onFormChange?.()
    }, [
      xItemQuantity,
      yItemQuantity,
      discount,
      expirationValue,
      selectedMenuItem,
      selectedMenuItemY,
      onFormChange
    ])

    return (
      <ContentBlock className={clsx(className, Styles.contentBlock)}>
        <div className={Styles.section}>
          <Text variant="heading-04">{t('Customer buys')}</Text>
          <Text variant="subtitle" className={Styles.negativeMargin}>
            {t('offers.create-buy-x-get-y.select-item-to-purchase')}
          </Text>
          <Text variant="heading-05">{t('Item')}</Text>
          {selectedMenuItem.length > 0 ? (
            <Button
              onClick={() => setSelectedMenuItem([])}
              variant="transparentOutlined"
              shape="rounded"
              size="large"
              className={Styles.buttonItem}>
              {selectedItem ? (
                <div className={Styles.itemContent}>
                  <div className={Styles.itemInfo}>
                    <Avatar
                      className={Styles.avatar}
                      image={selectedItem.avatar}
                      squared
                      size="medium"
                      string={getTranslation(selectedItem.name)}
                    />
                    <div className={Styles.itemText}>
                      <Text className={Styles.itemName}>
                        {selectedItem.name?.en}
                      </Text>
                      <Text className={Styles.itemPrice}>
                        {selectedItem.price} {currency}
                      </Text>
                    </div>
                  </div>
                  <Trash2 className={Styles.trashIcon} />
                </div>
              ) : (
                'Item not found'
              )}
            </Button>
          ) : (
            <Button
              onClick={toggleSelectItemsModalOpen}
              variant="transparentOutlined"
              shape="rounded"
              size="large"
              icon={<Plus className={Styles.buttonIcon} />}
              className={Styles.button}>
              {t('offers.create-buy-x-get-y.select-item')}
            </Button>
          )}
          <SelectMenuItemsModal
            isOpen={isSelectItemsModalOpen}
            onCloseModal={toggleSelectItemsModalOpen}
            initialMenuItemsIds={selectedMenuItem}
            onSubmit={itemsIds => {
              if (itemsIds.length > 0) {
                setSelectedMenuItem([itemsIds[0]])
              }
            }}
            multiple={false}
          />
          <Text variant="heading-05">{t('Quantity')}</Text>
          <Select onValueChange={setXItemQuantity} value={xItemQuantity}>
            <SelectTrigger className={clsx(Styles.selectTrigger, className)}>
              <SelectValue asChild>
                <Text>{xItemQuantity}</Text>
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              <ScrollArea viewportClassName={Styles.scrollArea}>
                {dummyQuantity.map((value: number) => (
                  <SelectItem key={value} value={String(value)}>
                    {value}
                  </SelectItem>
                ))}
              </ScrollArea>
            </SelectContent>
          </Select>
        </div>
        <hr className={Styles.dividerRow} />
        <div className={Styles.section}>
          <Text variant="heading-04">{t('Customer gets')}</Text>
          <Text variant="subtitle" className={Styles.negativeMargin}>
            {t('offers.create-buy-x-get-y.select-item-to-receive')}
          </Text>
          <Text variant="heading-05">{t('Item')}</Text>
          {selectedMenuItemY.length > 0 ? (
            <Button
              onClick={() => setSelectedMenuItemY([])}
              variant="transparentOutlined"
              shape="rounded"
              size="large"
              className={Styles.buttonItem}>
              {selectedItemY ? (
                <div className={Styles.itemContent}>
                  <div className={Styles.itemInfo}>
                    <Avatar
                      className={Styles.avatar}
                      image={selectedItemY.avatar}
                      squared
                      size="medium"
                      string={getTranslation(selectedItemY.name)}
                    />
                    <div className={Styles.itemText}>
                      <Text className={Styles.itemName}>
                        {selectedItemY.name?.en}
                      </Text>
                      <Text className={Styles.itemPrice}>
                        {selectedItemY.price} {currency}
                      </Text>
                    </div>
                  </div>
                  <Trash2 className={Styles.trashIcon} />
                </div>
              ) : (
                'Item not found'
              )}
            </Button>
          ) : (
            <Button
              onClick={toggleSelectItemsYModalOpen}
              variant="transparentOutlined"
              shape="rounded"
              size="large"
              icon={<Plus className={Styles.buttonIcon} />}
              className={Styles.button}>
              {t('offers.create-buy-x-get-y.select-item')}
            </Button>
          )}
          <SelectMenuItemsModal
            isOpen={isSelectItemsYModalOpen}
            onCloseModal={toggleSelectItemsYModalOpen}
            initialMenuItemsIds={selectedMenuItemY}
            onSubmit={itemsIds => {
              if (itemsIds.length > 0) {
                setSelectedMenuItemY([itemsIds[0]])
              }
            }}
            multiple={false}
          />
          <Text variant="heading-05">{t('Quantity')}</Text>
          <Select onValueChange={setYItemQuantity} value={yItemQuantity}>
            <SelectTrigger className={clsx(Styles.selectTrigger, className)}>
              <SelectValue asChild>
                <Text>{yItemQuantity}</Text>
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              <ScrollArea viewportClassName={Styles.scrollArea}>
                {dummyQuantity.map((value: number) => (
                  <SelectItem key={value} value={String(value)}>
                    {value}
                  </SelectItem>
                ))}
              </ScrollArea>
            </SelectContent>
          </Select>
          <Text variant="heading-05">
            {t('offers.create-buy-x-get-y.discount-value')}
          </Text>
          <RadioGroup
            onValueChange={setDiscount}
            defaultValue={String(discount)}
            className={Styles.radioGroup}>
            {DISCOUNT_VALUES.map((value, id) => {
              return (
                <RadioGroup.Row
                  key={id}
                  value={String(value)}
                  className={Styles.radioRow}>
                  {`${value}%`} Off
                </RadioGroup.Row>
              )
            })}
          </RadioGroup>
          <Separator variant="solid" />
          <div className={Styles.checkbox}>
            <Label layout="row" className={Styles.checkboxLabel}>
              <Checkbox
                checked={isCumulative}
                onCheckedChange={(checked: boolean) => setIsCumulative(checked)}
              />
              <Text variant="nextSubtitle" className={Styles.checkboxText}>
                {t('offers.create-buy-x-get-y.set-as-cumulative')}
                <Text
                  variant="nextBody"
                  asChild
                  className={Styles.checkboxValue}>
                  <span>
                    {t('offers.create-buy-x-get-y.cumulative-description')}
                  </span>
                </Text>
              </Text>
            </Label>
          </div>
          <hr className={Styles.dividerRow} />
          <Text variant="heading-04">{t('Offer details')}</Text>
          <Text variant="heading-05">{t('Expiration date')}</Text>
          <Select
            onValueChange={setExpirationValue}
            value={expirationValue}
            aria-label={t('offers.create-cart-discount.expiration-date.title')}>
            <SelectTrigger className={clsx(Styles.selectTrigger, className)}>
              <SelectValue asChild>
                <Text>{t(`offers.offers-details.${expirationValue}`)}</Text>
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              <ScrollArea viewportClassName={Styles.scrollArea}>
                {expirationValues.map(value => (
                  <SelectItem key={value} value={value}>
                    {t(`offers.offers-details.${value}`)}
                  </SelectItem>
                ))}
              </ScrollArea>
            </SelectContent>
          </Select>
        </div>
      </ContentBlock>
    )
  }
)

export default FormDetails
